/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { CellContext } from '@tanstack/react-table';
import { ShoppingCart } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Internal dependencies
 */
import { DeliveryAddress, PaymentStatus, Purchase } from '@Types/purchase';
import { ResourceTableView } from '@Components/ResourceTable';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { getPaymentTypes } from '@Utils/toast/purchaseHelpers';
import { getMemberProfile } from '@Utils/fish';
import { useGetPurchases } from '@Hooks/usePurchases';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';

const formatCurrency = (value: number | null) => {
  if (value === null || value === undefined) return '-';
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const orderColumnConfigs: ColumnConfig<Purchase>[] = [
  {
    id: 'confirmationNumber',
    headerLabel: 'Confirmation #',
    accessor: (row) => row.confirmationNumber ?? '-',
    type: 'text',
    size: 140,
  },
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) => getMemberProfile(row.fish)?.fName ?? '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) => getMemberProfile(row.fish)?.lName ?? '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'status',
    headerLabel: 'Payment Status',
    accessor: (row) => row.status,
    type: 'text',
    size: 100,
    cell: (info) => {
      const status = info.getValue() as PaymentStatus;
      return <Badge variant="secondary">{status}</Badge>;
    },
  },
  {
    id: 'price',
    headerLabel: 'Amount',
    accessor: (row) => row.price,
    type: 'currency',
    size: 100,
    cell: (info) => formatCurrency(info.getValue() as number),
    meta: { align: 'left' as const },
  },
  {
    id: 'paymentType',
    headerLabel: 'Payment Type',
    accessor: (row) => getPaymentTypes(row.order.checks),
    cell: (info: CellContext<Purchase, string[]>) => (
      <div className="flex flex-row gap-2">
        {info.getValue().map((type) => (
          <Badge key={`${info.cell.id}-payment-type-${type}`}>{type}</Badge>
        ))}
      </div>
    ),
    type: 'text',
    size: 120,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) =>
      row.createdAt ? DateTime.fromISO(row.createdAt).toMillis() : null,
    type: 'date',
    size: 180,
    cell: (info) => {
      const value = info.getValue() as number | null;
      return value
        ? DateTime.fromMillis(value).toLocaleString(DateTime.DATETIME_MED)
        : '-';
    },
  },
  {
    id: 'deliveryAddress',
    headerLabel: 'Fullfillment',
    accessor: (row) => row.deliveryAddress,
    type: 'text',
    size: 120,
    cell: (info: CellContext<Purchase, string[]>) => {
      const address = info.getValue() as DeliveryAddress | null;

      if (!address) {
        return 'Pickup';
      }

      return 'Delivery';
    },
    enableSorting: false,
  },
];

const ORDER_FIELD_MAPPING: Record<string, string> = {
  fName: 'filter[fish][fishProfiles][some][fName]',
  lName: 'filter[fish][fishProfiles][some][lName]',
};

const ORDER_STATIC_PARAMS: Record<string, string> = {
  'filter[deletedAt]': null,
  'include[fish][include][fishProfiles][filter][deletedAt]': 'null',
};

export default function ShopOrdersList() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Purchase>({
    columnsConfig: orderColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    baseSearchParams: () => ({
      'include': 'fish.fishProfiles,deliveryAddress',
      'filter[productDescription]': '*Stork Club Shop Order*',
      'filter[status]': 'SUCCESS',
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: ORDER_FIELD_MAPPING,
        staticParams: ORDER_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'confirmationNumber', label: 'Confirmation #' },
      { field: 'fName', label: 'First Name' },
      { field: 'lName', label: 'Last Name' },
    ],
    initialPageSize: 10,
  });

  const {
    data: purchases,
    status,
    isError,
    error,
  } = useGetPurchases(controller.searchParams);

  useEffect(() => {
    if (isError) {
      toastError(error.message);
    }
  }, [isError, error]);

  const rows: Purchase[] = useMemo(() => {
    return purchases?.data ?? [];
  }, [purchases?.data]);

  return (
    <ResourceTableView<Purchase>
      label="Orders"
      controller={controller}
      data={rows}
      meta={purchases?.meta}
      status={status}
      summaryIcon={<ShoppingCart className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Orders: "
      onRowClick={(row) => navigate(`/shop/orders/${row.id}`)}
    />
  );
}
